import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from 'antd';
import dayjs from 'dayjs';

import { UserContext } from '@/app/UserProvider';
import type { ActivePlan, UpgradablePlan } from '@/services/subscriptionService';

import decorDefault from '../images/plan-right-default.svg';
import decorFree from '../images/plan-right-free.svg';
import decorPro from '../images/plan-right-pro.svg';
import decorUltra from '../images/plan-right-ultra.svg';

// 背景渐变色（兜底色：linear-gradient(276deg, #F7F7F7 0%, #D5E2F8 95.28%)）
const PLAN_BG_GRADIENT: Partial<Record<string, string>> = {
  Free: 'linear-gradient(276deg, #F7F7F7 0%, #F7F7F7 95.28%)',
  Pro: 'linear-gradient(299deg, #F7F7F7 0%, #FFEDDF 100%)',
  'Pro+': 'linear-gradient(276deg, #F7F7F7 0%, #D5F8F4 95.28%)',
  Ultra: 'linear-gradient(276deg, #F7F7F7 0%, #D5F8F4 95.28%)',
};

const DEFAULT_BG_GRADIENT = 'linear-gradient(276deg, #F7F7F7 0%, #D5E2F8 95.28%)';

// 右侧装饰图（未知套餐使用 decorDefault）
const PLAN_DECOR_SRC: Partial<Record<string, string>> = {
  Free: decorFree,
  Pro: decorPro,
  Ultra: decorUltra,
  'Pro+': decorUltra,
};

interface PlanCardProps {
  plan: ActivePlan;
  nextPlans?: ActivePlan[];
  upgradablePlans: UpgradablePlan[];
  onCancelRenew: (plan: ActivePlan) => void;
  onUpgrade: (plan: ActivePlan) => void;
}

export function PlanCard({
  plan,
  nextPlans = [],
  upgradablePlans,
  onCancelRenew,
  onUpgrade,
}: PlanCardProps) {
  const { t } = useTranslation();
  const { showPricing } = useContext(UserContext);
  // 背景渐变色和背景图
  const bgGradient = PLAN_BG_GRADIENT[plan.planName] ?? DEFAULT_BG_GRADIENT;
  const decorSrc = PLAN_DECOR_SRC[plan.planName] ?? decorDefault;
  // cancelAutoRenewEnabled === true 表示可以取消续订
  const canCancel = plan.cancelAutoRenewEnabled === true;
  // 是否显示升级按钮：upgradablePlans 有值且不为空
  const showUpgradeButton = upgradablePlans && upgradablePlans.length > 0;

  // 订阅有效期
  const formattedStart = plan.effectiveStart ? dayjs(plan.effectiveStart).format('YYYY-MM-DD') : '';
  const formattedEnd = plan.effectiveEnd ? dayjs(plan.effectiveEnd).format('YYYY-MM-DD') : '';

  return (
    <div className="relative flex w-full flex-col items-start justify-center gap-5 overflow-hidden p-10">
      {/* 渐变背景色 */}
      <div
        className="pointer-events-none absolute inset-0 z-0"
        style={{ background: bgGradient }}
      />

      {/* 右侧装饰图 */}
      <img
        src={decorSrc}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-0 z-0 h-full select-none object-contain"
      />

      {/* 上行：计划名 + 按钮 */}
      <div className="relative flex h-auto w-full flex-col items-start gap-4 md:h-12 md:flex-row md:items-center md:justify-between md:gap-0">
        {/* 左侧：计划大标题 + 当前计划 */}
        <div className="flex min-w-0 flex-1 items-baseline gap-2 text-[#111314]">
          <span className="shrink-0 text-[32px] font-medium leading-[32px] tracking-[-1.6px] md:text-[48px] md:leading-[48px] md:tracking-[-2.4px]">
            {plan.planName}
          </span>
          <span className="min-w-0 truncate text-[12px] font-normal leading-[130%] opacity-80">
            {t('usageTab.planTab.planCard.currentPlan')}
          </span>
        </div>

        {/* 右侧：按钮区 */}
        <div className="flex w-full shrink-0 items-center gap-3 md:w-auto md:gap-5">
          {/* 升级计划按钮（根据接口的upgradablePlans是否有值来显示） */}
          {showUpgradeButton && showPricing && (
            <button
              type="button"
              onClick={() => onUpgrade(plan)}
              className="flex h-[38px] w-[120px] cursor-pointer items-center justify-center gap-[10px] rounded border border-[#111314] bg-[#111314] px-4 py-[7px] md:w-[132px] md:px-[33px]"
            >
              <span className="whitespace-nowrap text-[16px] font-medium text-white [font-family:'PingFang_SC']">
                {t('usageTab.planTab.planCard.upgradePlan')}
              </span>
            </button>
          )}

          {/* 取消续订按钮（Free 和 elite 不显示） */}
          {plan.planName !== 'Free' && plan.planName !== 'Elite' && (
            <Tooltip
              title={canCancel ? '' : t('usageTab.planTab.planCard.autoRenewDisabled')}
              color="#FFFFFF"
              placement="top"
              overlayInnerStyle={{
                fontSize: '14px',
                color: '#252626',
                borderRadius: '4px',
                padding: '5px 8px',
              }}
            >
              <button
                type="button"
                onClick={canCancel ? () => onCancelRenew(plan) : undefined}
                className={[
                  "whitespace-nowrap border-none bg-transparent p-0 text-[14px] font-normal leading-[22px] [font-family:'PingFang_SC']",
                  canCancel ? 'cursor-pointer text-[#898A8C]' : 'cursor-not-allowed text-[#C5C6C9]',
                ].join(' ')}
              >
                {t('usageTab.planTab.planCard.cancelRenew')}
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* 下行：底部说明文字 */}
      <div className="relative flex flex-col gap-1">
        <span className="text-[12px] font-normal leading-[130%] text-[#111314] opacity-50">
          {plan.planName === 'Free'
            ? t('usageTab.planTab.planCard.freeFooter')
            : t('usageTab.planTab.planCard.paidFooter', {
                start: formattedStart,
                end: formattedEnd,
                planType: plan.planName,
              })}
        </span>
        {/* 即将生效计划 */}
        {nextPlans.map((nextPlan) => {
          const nextFormattedStart = nextPlan.effectiveStart
            ? dayjs(nextPlan.effectiveStart).format('YYYY-MM-DD')
            : '';
          const nextFormattedEnd = nextPlan.effectiveEnd
            ? dayjs(nextPlan.effectiveEnd).format('YYYY-MM-DD')
            : '';
          return (
            <span
              key={nextPlan.subscriptionId}
              className="text-[12px] font-normal leading-[130%] text-[#111314] opacity-50"
            >
              {t('usageTab.planTab.planCard.nextPlanFooter', {
                start: nextFormattedStart,
                end: nextFormattedEnd,
                planType: nextPlan.planName,
              })}
            </span>
          );
        })}
      </div>
    </div>
  );
}
